import {useImmersionMode, useLocalePrefix, useRoute} from '#imports';
import {
    AutocompleteOrganizationApi,
    type HTTPHeaders,
    AdvancedSearchApi,
    AdvancedSearchAuthenticatedApi,
    AutocompleteSkillApi,
    BusinessSectorsApi,
    CategoryPredictionsApi,
    Configuration,
    PoolOfFreelancersApi,
    SearchProfilesApi,
    MicroConsultingSearchApi,
    ShortlistApi,
    SuggestSkillsApi,
    ClientOnboardingApi,
    AdminApi,
    SourcingSearchApi,
} from '@api';

class CustomHeadersConfiguration extends Configuration {
    private readonly getCustomHeaders: () => HTTPHeaders;

    constructor({getCustomHeaders}: {getCustomHeaders: () => HTTPHeaders}) {
        super({basePath: ''});
        this.getCustomHeaders = getCustomHeaders;
    }

    override get headers(): HTTPHeaders | undefined {
        return {
            ...super.headers,
            ...this.getCustomHeaders(),
        };
    }
}

export function useApis() {
    const route = useRoute();
    const {getImmersionHeaderFromCurrentRoute} = useImmersionMode();
    const {getLocalePrefix, getLocaleCodeFromPrefix} = useLocalePrefix();
    const apiConfiguration = new CustomHeadersConfiguration({
        getCustomHeaders: () => ({
            ...getImmersionHeaderFromCurrentRoute(route),
            ...getLocaleHeaderFromUrlPrefix(),
        }),
    });

    // The backend never sees the /en-be page path: X-LANG is its only way to resolve the locale from the URL prefix.
    function getLocaleHeaderFromUrlPrefix(): HTTPHeaders {
        const localePrefix = getLocalePrefix();
        const localeCode = localePrefix ? getLocaleCodeFromPrefix(localePrefix) : undefined;

        return localeCode ? {'x-lang': localeCode} : {};
    }

    const adminApi = new AdminApi(apiConfiguration);
    const advancedSearchApi = new AdvancedSearchApi(apiConfiguration);
    const advancedSearchAuthenticatedApi = new AdvancedSearchAuthenticatedApi(apiConfiguration);
    const poolOfFreelancersApi = new PoolOfFreelancersApi(apiConfiguration);
    const searchApi = new SearchProfilesApi(apiConfiguration);
    const microConsultingApi = new MicroConsultingSearchApi(apiConfiguration);
    const shortlistApi = new ShortlistApi(apiConfiguration);
    const autocompleteSkillApi = new AutocompleteSkillApi(apiConfiguration);
    const suggestSkillsApi = new SuggestSkillsApi(apiConfiguration);
    const autocompleteOrganizationApi = new AutocompleteOrganizationApi(apiConfiguration);
    const businessSectorsApi = new BusinessSectorsApi(apiConfiguration);
    const categoryPredictionsApi = new CategoryPredictionsApi(apiConfiguration);
    const clientOnboardingApi = new ClientOnboardingApi(apiConfiguration);
    const sourcingSearchApi = new SourcingSearchApi(apiConfiguration);

    return {
        adminApi,
        advancedSearchApi,
        advancedSearchAuthenticatedApi,
        categoryPredictionsApi,
        poolOfFreelancersApi,
        searchApi,
        microConsultingApi,
        shortlistApi,
        autocompleteSkillApi,
        suggestSkillsApi,
        autocompleteOrganizationApi,
        businessSectorsApi,
        clientOnboardingApi,
        sourcingSearchApi,
    };
}
